/**
 * Manifest — the Cloud Manifest, in the platform (EP-004P).
 *
 * == What this replaces ==================================================
 *
 * A per-facility spreadsheet the providers keep in SharePoint and work from
 * on site. April calls it their bible. Two real sheets for LAKE CITY were
 * used to build this, and all 39 Patient IDs on them already resolve to rows
 * in `patients` — so this is a view of data the platform holds, not a new
 * place to keep it.
 *
 * The columns are theirs, in their order: room, resident, status, tags,
 * alert, notes. Renaming them into tidier platform vocabulary would make the
 * screen unrecognisable to the people who use it every day, and the tidier
 * vocabulary would be ours rather than true.
 *
 * == What the spreadsheet cannot do, and this does =======================
 *
 * `qualification_status` is a list. 26 residents are both MEDICAID PENDING
 * and QUALIFIED, and a single STATUS cell has to drop one. Every value is
 * shown here.
 *
 * The 9.9 sheet also has all ten columns duplicated — a paste artifact from
 * building it off the previous week's copy. That class of error stops
 * existing once the manifest is read rather than rebuilt.
 *
 * == Why so much of it may be withheld ===================================
 *
 * Alert, Notes and Tags are clinical narrative — what a resident disclosed
 * during an exam, what the clinician concluded. They are released only for
 * residents the viewer is care-assigned to. Someone reviewing the portal sees
 * a true census and no charts, which is the point rather than a limitation.
 *
 * The screen says which state it is in, per row, instead of showing blanks
 * that read as "nothing recorded".
 */

import React, { useMemo, useState } from 'react';
import { StateBlock as TideState } from '../../components/tide';
import {
  DaySheet, RosterPatient, AtdSchedule, RosterLens, RosterOrder, ROSTER_LENSES, isLens, lensLabel, patientName, TESTING_STATUS_LABEL, testingTone, testingTitle, monthYear, shortDate,
  ManifestField, ManifestPatch, manifestLinkOf,
} from './providerApi';

/** The outcome of a manifest save, in the provider's words: null when it took, otherwise why not. */
export type ManifestSaveOutcome = string | null;

/**
 * The lenses, as one row: Census · Active · Enrolled as chips with the
 * building's counts, and the rest under More. A control, not a page, and
 * not Monday's coloured group wall. Every predicate and every count is the
 * server's, so a chip can never disagree with its list.
 *
 * Buttons and a select, not clickable divs: the chips carry aria-pressed,
 * the More select is labelled, and the count is in each chip's accessible
 * name. Nothing here is told by colour alone.
 */
function LensBar({ lens, counts, onChange }: { lens: RosterLens; counts: Partial<Record<RosterLens, number>> | null | undefined; onChange: (l: RosterLens) => void }) {
  const primary = ROSTER_LENSES.filter((l) => l.primary);
  const more = ROSTER_LENSES.filter((l) => !l.primary);
  const inMore = more.some((l) => l.key === lens);
  const n = (k: RosterLens) => (counts && counts[k]) ?? 0;
  return (
    <div className="pv-chips" role="group" aria-label="Census view">
      {primary.map((l) => (
        <button key={l.key} type="button" className={`pv-chip${lens === l.key ? ' on' : ''}`} aria-pressed={lens === l.key} title={l.what} onClick={() => onChange(l.key)}>
          {l.label} <span className="pv-count">{n(l.key)}</span>
        </button>
      ))}
      <label className={`pv-chip-more${inMore ? ' on' : ''}`}>
        <span className="pv-sr-only">More views</span>
        <select value={inMore ? lens : ''} onChange={(e) => { if (isLens(e.target.value)) onChange(e.target.value); }} aria-label="More views">
          <option value="" disabled>More…</option>
          {more.map((l) => <option key={l.key} value={l.key} title={l.what}>{l.label} · {n(l.key)}</option>)}
        </select>
      </label>
    </div>
  );
}

/** What an empty lens says: the lens by name, never "no residents" when the building has plenty. */
function emptyCopy(lens: RosterLens, confirmed: number): string {
  if (confirmed === 0) return 'No residents are currently confirmed at this facility.';
  switch (lens) {
    case 'manifest': return 'Nobody here needs the provider on this visit: every census resident is screened DNQ with an annual not yet due.';
    case 'census': return 'No resident here is Active or Prospect on the board.';
    case 'active': return 'No active or prospect residents at this facility.';
    case 'enrolled': return 'No enrolled residents at this facility.';
    case 'atd': return 'Nobody here is approved to dispense right now.';
    case 'inactive': return 'No inactive residents at this facility.';
    case 'deceased': return 'No deceased residents on the record at this facility.';
    case 'no-status': return 'Every confirmed resident here has a status recorded.';
    default: return `Nothing in ${lensLabel(lens)} at this facility.`;
  }
}

/** How a provider walks the building: by room, or by name. */
function OrderToggle({ order, onChange }: { order: RosterOrder; onChange: (o: RosterOrder) => void }) {
  return (
    <label className="pv-order">
      <span>Order</span>
      <select value={order} onChange={(e) => onChange(e.target.value as RosterOrder)} aria-label="Row order">
        <option value="room">By room</option>
        <option value="name">By name</option>
      </select>
    </label>
  );
}

/**
 * Testing status — one cell, one word, and the conflict named when there
 * is one. Dagmar Blunt's row carried NO MEDICAID and QUALIFIED together;
 * showing both handed the provider contradictory instructions. Now it says
 * "Needs verification · conflicting" and the two words on hover.
 */
function TestingCell({ patient }: { patient: RosterPatient }) {
  if (!patient.testing_status) {
    // CounselEAR has been read and tags nothing on the list: say so, and say
    // what Monday still claims, rather than showing a dash that reads as
    // "unknown" when the record was actually consulted.
    if (patient.testing_status_source === 'COUNSELEAR') {
      return (
        <span className="pv-none" title={testingTitle(patient)}>
          not on record{patient.testing_status_monday ? ` · Monday: ${TESTING_STATUS_LABEL[patient.testing_status_monday]}` : ''}
        </span>
      );
    }
    return <span className="pv-none">—</span>;
  }
  const tone = testingTone(patient.testing_status);
  return (
    <span className={`pv-tag${tone ? ` ${tone}` : ''}`} title={testingTitle(patient)}>
      {TESTING_STATUS_LABEL[patient.testing_status]}
      {patient.testing_status_conflict ? ' · conflicting' : ''}
      {patient.testing_status_drift ? ' · Monday differs' : ''}
    </span>
  );
}

/** Their column, their placeholder. An empty cell reads as a value. */
function Cell({ value }: { value: string | null | undefined }) {
  return <>{value && value.trim() ? value : <span className="pv-none">—</span>}</>;
}

function Tags({ values }: { values: string[] | null | undefined }) {
  if (!values || values.length === 0) return <span className="pv-none">—</span>;
  return (
    <span className="pv-tags">
      {values.map((t) => <span className="pv-tag" key={t}>{t}</span>)}
    </span>
  );
}

/**
 * One resident.
 *
 * A care-assigned row carries the clinical columns. An unassigned row carries
 * the census and says so once, rather than rendering three empty cells that
 * look like a resident with nothing recorded against them.
 */
/** Comma-separated tags from a text box, trimmed, de-duplicated, upper-cased the way the sheet keeps them. */
function parseTags(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(',')) {
    const t = raw.trim().toUpperCase();
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}

/**
 * The provider's own columns, edited in place (workforce cutover, 2026-09-19).
 *
 * Offered only on a care-assigned row, and only for the columns the server
 * named as eMerge's: on production today that is Notes and Provider tags;
 * Alert and the office's qualification words stay the board's, and the
 * testing tags are CounselEAR's. The row says which is which rather than
 * greying out cells without a word. Saving sends exactly the fields that
 * changed; the server checks care assignment and field authority again.
 */
function RowEditor({ patient, fields, onSave, onClose }: { patient: RosterPatient; fields: ManifestField[]; onSave: (patientId: string, patch: ManifestPatch) => Promise<ManifestSaveOutcome>; onClose: () => void }) {
  const canNotes = fields.includes('notes');
  const canAlerts = fields.includes('alerts');
  const canProviderTags = fields.includes('provider_tags');
  const [notes, setNotes] = useState(patient.notes || '');
  const [alerts, setAlerts] = useState(patient.alerts || '');
  const [providerTags, setProviderTags] = useState((patient.provider_tags || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = patientName(patient);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: ManifestPatch = {};
    if (canNotes && notes !== (patient.notes || '')) patch.notes = notes.trim() ? notes.trim() : null;
    if (canAlerts && alerts !== (patient.alerts || '')) patch.alerts = alerts.trim() ? alerts.trim() : null;
    if (canProviderTags) {
      const next = parseTags(providerTags);
      const prev = patient.provider_tags || [];
      if (next.join(' ') !== prev.join(' ')) patch.provider_tags = next.length ? next : null;
    }
    if (Object.keys(patch).length === 0) { onClose(); return; }
    setSaving(true); setError(null);
    const outcome = await onSave(patient.id, patch);
    setSaving(false);
    if (outcome) setError(outcome); else onClose();
  };

  return (
    <td colSpan={3} className="pv-mf-edit">
      <form onSubmit={submit} aria-label={`Edit manifest for ${who}`}>
        {canProviderTags && (
          <label>
            <span>Provider tags</span>
            <input value={providerTags} onChange={(e) => setProviderTags(e.target.value)} placeholder="Comma-separated" disabled={saving} />
          </label>
        )}
        <label>
          <span>Alert{canAlerts ? '' : ' · scheduling board'}</span>
          {canAlerts
            ? <textarea value={alerts} onChange={(e) => setAlerts(e.target.value)} rows={2} disabled={saving} />
            : <span className="pv-mf-ro"><Cell value={patient.alerts} /></span>}
        </label>
        <label>
          <span>Notes{canNotes ? '' : ' · scheduling board'}</span>
          {canNotes
            ? <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={saving} />
            : <span className="pv-mf-ro"><Cell value={patient.notes} /></span>}
        </label>
        {error && <p className="pv-mf-err" role="alert">{error}</p>}
        <div className="pv-mf-actions">
          <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </form>
    </td>
  );
}

/** Native ATD: where the approved-to-dispense resident stands on the schedule (atd lens only). */
function AtdBadge({ schedule }: { schedule: AtdSchedule }) {
  let text = 'Not yet scheduled';
  let tone = 'attn';
  if (schedule.on_this_visit) { text = "On this visit's manifest"; tone = 'ok'; }
  else if (schedule.state === 'ON_MANIFEST') { text = `On the manifest for ${schedule.visit_on ? shortDate(schedule.visit_on) : 'a later visit'}`; tone = 'ok'; }
  else if (schedule.state === 'PIT_STOP') { text = `Pit stop ${schedule.pit_stop_on ? shortDate(schedule.pit_stop_on) : ''}`.trim(); tone = 'ok'; }
  // A plan that no longer holds says so rather than reading as done.
  const stale: Record<NonNullable<AtdSchedule['attention']>, string> = {
    VISIT_OTHER_BUILDING: 'was planned at another building', VISIT_CANCELLED: 'visit cancelled', VISIT_COMPLETED: 'visit completed',
    VISIT_PASSED: 'visit date passed', PIT_STOP_PASSED: 'pit stop date passed',
  };
  if (schedule.attention) { text = `${text} (${stale[schedule.attention]})`; tone = 'attn'; }
  return <span className={`pv-tag ${tone}`} data-testid="atd-schedule">{text}</span>;
}

function ManifestRow({ patient, fields, onSave }: { patient: RosterPatient; fields: ManifestField[]; onSave: ((patientId: string, patch: ManifestPatch) => Promise<ManifestSaveOutcome>) | null }) {
  const assigned = patient.care_assigned;
  const [editing, setEditing] = useState(false);
  const editable = assigned && Boolean(onSave) && fields.length > 0;

  return (
    <tr className={assigned ? '' : 'pv-mf-unassigned'}>
      <td className="pv-mf-room">{patient.room_number || <span className="pv-none">—</span>}</td>
      <td className="pv-mf-who">
        <strong>{patientName(patient)}</strong>
        {patient.atd_schedule && <AtdBadge schedule={patient.atd_schedule} />}
        {(patient.gender || patient.annual_due || patient.new_referral || patient.manifest_exclusion) && (
          <span className="pv-mf-sub">
            {patient.gender || ''}
            {/* Prospect is the business's word for a new referral (April, 2026-09-11): said, not inferred. */}
            {patient.new_referral && <span className="pv-tag ok">new referral</span>}
            {patient.annual_due && <span className="pv-tag attn">annual due</span>}
          </span>
        )}
        {/*
          In the census, off the manifest: the row says why and when they are
          back — screened DNQ on the board's DNQ date, annual in month/year
          precision only (the business ball-parks it; a day would be invented).
        */}
        {patient.manifest_exclusion === 'SCREENED_DNQ' && (
          <span className="pv-mf-sub pv-none">
            Not on the current manifest — screened{patient.dnq_date ? ` ${shortDate(patient.dnq_date)}` : ''}, DNQ
            {monthYear(patient.annual_appt_date) ? ` · annual ${monthYear(patient.annual_appt_date)}` : ' · annual not recorded'}
          </span>
        )}
      </td>
      {/*
        The CounselEAR id: released for a care-assigned resident, and said to
        be withheld otherwise.

        Not shown for everyone. The id is a chart locator, and these providers
        hold CounselEAR logins — the id plus that login opens the whole record,
        so handing it over for a resident they are not assigned to hands over
        the chart. That is a business decision of 25 August 2026: a
        practitioner assigned to a BUILDING does not thereby get every chart in
        it. The server enforces it by never fetching the value for those rows.

        What this cell owes the reader is the difference between the two
        empties. A dash reads as "this resident has no chart"; the truth is
        "not released to you". Saying so costs one word.
      */}
      <td className="pv-mf-cid">
        {assigned
          ? (patient.counselear_id || <span className="pv-none">—</span>)
          : <span className="pv-mf-withheld">withheld</span>}
      </td>
      {/*
        Three concepts, three cells (Provider ruling 2026-09-08). Resident
        status answers "is this person part of the census". Testing status
        answers "should I screen them, or does someone need to verify first".
        Office tags answer "what is happening downstream". They used to share
        one cell, and Thomas Canavan — empty qualification, "DNQ- PL TOO LOW"
        in the withheld tag column — read as plain "Active".
      */}
      <td>
        {patient.status
          ? <span className="pv-mf-status">{patient.status}{patient.enrolled ? <span className="pv-tag ok">enrolled</span> : null}</span>
          : <span className="pv-none">—</span>}
      </td>
      <td><TestingCell patient={patient} /></td>
      <td><Tags values={patient.office_tags} /></td>
      {assigned ? (
        editing && onSave ? (
          <RowEditor patient={patient} fields={fields} onSave={onSave} onClose={() => setEditing(false)} />
        ) : (
          <>
            <td><Tags values={[...(patient.qualification_status || []), ...(patient.tags || []), ...(patient.provider_tags || [])]} /></td>
            <td className="pv-mf-alert"><Cell value={patient.alerts} /></td>
            <td className="pv-mf-notes"><Cell value={patient.notes} /></td>
          </>
        )
      ) : (
        <td className="pv-mf-withheld" colSpan={3}>
          Not care-assigned — tags, alert and notes not released
        </td>
      )}
      <td className="pv-mf-act">
        {editable && !editing && (
          <button type="button" className="pv-mf-editbtn" onClick={() => setEditing(true)} aria-label={`Edit manifest for ${patientName(patient)}`}>Edit</button>
        )}
      </td>
    </tr>
  );
}

/**
 * The workbooks that sit beside this screen.
 *
 * The cloud manifest is this facility's, recorded on the scheduling board.
 * The stock report is one workbook for the whole team, kept by the office.
 * Both are opened while working the manifest, so they are here rather than a
 * tab away, and both leave the platform, so they say so and open in a new
 * tab. `rel="noreferrer"` matters more than usual: these URLs may be
 * pre-authenticated, and a Referer header would hand one to whatever the
 * provider visits next.
 *
 * A missing cloud manifest is stated. It means the link is absent from the
 * scheduling board, which is a fact for the scheduling team, not a screen to
 * tidy away.
 */
function Workbooks({ manifest, stockReportUrl }: { manifest: { url: string; reference: boolean } | null; stockReportUrl: string | null }) {
  const manifestUrl = manifest ? manifest.url : null;
  if (!manifestUrl && !stockReportUrl) return null;
  return (
    <div className="pv-mf-books">
      <span className="pv-mf-books-label">Workbooks</span>
      {manifestUrl ? (
        <a className="pv-link" href={manifestUrl} target="_blank" rel="noreferrer noopener">
          {manifest && manifest.reference ? 'Cloud manifest (Monday reference) ↗' : 'Cloud manifest ↗'}
        </a>
      ) : (
        <span className="pv-none">
          No cloud manifest on the scheduling board for this facility
        </span>
      )}
      {stockReportUrl && (
        <a className="pv-link" href={stockReportUrl} target="_blank" rel="noreferrer noopener">
          Stock report ↗
        </a>
      )}
    </div>
  );
}

export default function Manifest({
  sheet, onPage, onLens, onOrder, stockReportUrl = null, onSave = null,
}: {
  sheet: DaySheet;
  onPage: (offset: number) => void;
  onLens: (l: RosterLens) => void;
  onOrder: (o: RosterOrder) => void;
  stockReportUrl?: string | null;
  /** Save one resident's provider-owned columns; null when the shell offers no write. */
  onSave?: ((patientId: string, patch: ManifestPatch) => Promise<ManifestSaveOutcome>) | null;
}) {
  const { roster, facility, counts } = sheet;
  const [onlyMine, setOnlyMine] = useState(false);
  // The server names the columns it would accept; a payload from before the deploy names none.
  // The room (20261022a) is edited in the field workspace's own box, not in this row editor.
  const writableFields: ManifestField[] = (sheet.provenance.writable ? (sheet.provenance.writableFields || []) : []).filter((f) => f !== 'room');
  const canEdit = Boolean(onSave) && writableFields.length > 0;

  // The building's link as eMerge holds it (MAN-4, 2026-10-05); Monday's
  // column only as a labelled reference where eMerge holds none yet.
  const manifestLink = useMemo(() => manifestLinkOf(sheet), [sheet]);

  const rows = useMemo(
    () => (onlyMine ? roster.rows.filter((r) => r.care_assigned) : roster.rows),
    [roster.rows, onlyMine]
  );

  const pageEnd = roster.offset + roster.returned;
  const hasMore = pageEnd < roster.total;

  return (
    <>
      <section className="pv-card">
        <div className="pv-card-head">
          <div>
            <h2>Manifest</h2>
            <p className="why">
              {facility.name} — everyone with a confirmed placement here.
            </p>
          </div>
          <label className="pv-mf-filter">
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
            />
            {/* Filters this page, and says so — it is not a search across the building. */}
            Only my residents ({counts.careAssignedOnPage} on this page)
          </label>
        </div>

        <Workbooks manifest={manifestLink} stockReportUrl={stockReportUrl} />

        <div className="pv-mf-controls">
          <LensBar lens={roster.lens} counts={roster.lenses} onChange={onLens} />
          <OrderToggle order={roster.order} onChange={onOrder} />
        </div>

        {roster.rows.length === 0 ? (
          <TideState kind="empty" reason={roster.confirmed === 0 ? 'none' : 'filtered'} message={emptyCopy(roster.lens, roster.confirmed)} />
        ) : rows.length === 0 ? (
          <p className="pv-empty">
            None of the residents on this page are assigned to you.
          </p>
        ) : (
          <div
            className="pv-mf-scroll"
            tabIndex={0}
            role="region"
            aria-label={`Manifest for ${facility.name}. Scroll horizontally to see all columns.`}
          >
            <table className="pv-mf">
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Resident</th>
                  <th>CounselEAR ID</th>
                  <th>Resident status</th>
                  <th>Testing status</th>
                  <th>Office tags</th>
                  <th>Tags</th>
                  <th>Alert</th>
                  <th>Notes</th>
                  <th className="pv-mf-act"><span className="pv-sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => <ManifestRow key={p.id} patient={p} fields={writableFields} onSave={canEdit ? onSave : null} />)}
              </tbody>
            </table>
          </div>
        )}

        {roster.total > roster.returned && (
          <div className="pv-pager">
            <span className="num">{roster.offset + 1}–{pageEnd} of {roster.total}</span>
            <div className="btns">
              <button
                type="button"
                disabled={roster.offset === 0}
                onClick={() => onPage(Math.max(0, roster.offset - roster.limit))}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!hasMore}
                onClick={() => onPage(roster.offset + roster.limit)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="pv-foot">
        <div className="pv-note">
          {canEdit ? (
            <span>
              <strong>Your columns are yours to change.</strong>{' '}
              {writableFields.includes('notes') && writableFields.includes('provider_tags') ? 'Notes and provider tags' : writableFields.join(', ')} can be edited on any resident you are care-assigned to; each change is recorded against your account. {sheet.provenance.writeBlockedBy}.
            </span>
          ) : (
            <span>
              <strong>This screen is read-only.</strong>{' '}
              {sheet.provenance.writeBlockedBy}.
            </span>
          )}
          <span>
            Resident status and office tags are shown exactly as recorded. Testing status is one
            word derived from the qualification field and the approved status words in the tag
            column; a record that says both qualified and not is marked conflicting for the
            verification VA rather than guessed at.
          </span>
          <span>
            Once a resident's CounselEAR record has been read, testing status comes from
            CounselEAR. Changes made in CounselEAR normally appear here within about five
            minutes under healthy operating conditions.
          </span>
        </div>
      </div>
    </>
  );
}
